import { Component, OnInit, AfterViewInit, inject, signal, PLATFORM_ID, ElementRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { SeoService } from '../../core/services/seo.service';
import { SiteConfigService } from '../../core/config/site-config.service';
import { BlogPost } from '../../core/config/site-config.interface';

interface TocItem {
  id: string;
  text: string;
}

@Component({
  selector: 'app-blog-post',
  standalone: true,
  imports: [RouterLink],
  template: `
    @if (post) {
      <!-- Hero -->
      <section class="relative min-h-[400px] md:min-h-[480px] flex items-end -mt-20 overflow-hidden">
        <img [src]="post.image" [alt]="post.title" class="absolute inset-0 w-full h-full object-cover" />
        <div class="absolute inset-0 bg-gradient-to-t from-surface-950/90 via-surface-950/40 to-surface-950/20"></div>
        <div class="relative z-10 w-full pb-12 pt-40 md:pt-48">
          <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <a routerLink="/blog" class="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white mb-6 transition-colors">
              <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
              Back to Blog
            </a>
            <div class="flex flex-wrap items-center gap-3 mb-4">
              <span class="px-3 py-1 bg-brand-500/20 text-brand-300 text-[0.65rem] font-bold uppercase tracking-wider rounded-md">{{ post.category }}</span>
              <span class="text-sm text-white/50">{{ post.date }}</span>
              <span class="text-white/30">&middot;</span>
              <span class="text-sm text-white/50">{{ post.readTime }}</span>
            </div>
            <h1 class="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] tracking-tight max-w-3xl">
              {{ post.title }}
            </h1>
          </div>
        </div>
      </section>

      <!-- Author + Share Bar -->
      <section class="bg-white border-b border-surface-100">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
          <div class="flex items-center justify-between py-5">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-bold text-xs">
                {{ post.author.initials }}
              </div>
              <div>
                <div class="text-sm font-semibold text-surface-900">{{ post.author.name }}</div>
                <div class="text-xs text-surface-400">{{ post.author.role }}</div>
              </div>
              <div class="hidden sm:block w-px h-8 bg-surface-200 mx-2"></div>
              <div class="hidden sm:flex items-center gap-3 text-xs text-surface-400">
                <time>{{ post.date }}</time>
                <span>&middot;</span>
                <span>{{ post.readTime }}</span>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <button (click)="sharePost('twitter')" aria-label="Share on X/Twitter"
                      class="w-9 h-9 rounded-lg bg-surface-50 text-surface-400 hover:bg-surface-100 hover:text-surface-600 flex items-center justify-center transition-colors">
                <svg aria-hidden="true" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </button>
              <button (click)="sharePost('linkedin')" aria-label="Share on LinkedIn"
                      class="w-9 h-9 rounded-lg bg-surface-50 text-surface-400 hover:bg-surface-100 hover:text-surface-600 flex items-center justify-center transition-colors">
                <svg aria-hidden="true" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/></svg>
              </button>
              <button (click)="sharePost('copy')" aria-label="Copy link"
                      class="w-9 h-9 rounded-lg bg-surface-50 text-surface-400 hover:bg-surface-100 hover:text-surface-600 flex items-center justify-center transition-colors">
                <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-1.02a4.5 4.5 0 00-1.242-7.244l-4.5-4.5a4.5 4.5 0 00-6.364 6.364L4.25 8.81" /></svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Article Body -->
      <section class="py-12 md:py-16 bg-white">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
          <div class="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-12 lg:gap-16">
            <!-- Content -->
            <div>
              <!-- Excerpt -->
              <p class="text-lg md:text-xl text-surface-600 leading-relaxed mb-10 font-medium">
                {{ post.excerpt }}
              </p>

              <!-- Article HTML -->
              <div class="blog-prose" [innerHTML]="post.content"></div>

              <!-- Tags -->
              <div class="flex flex-wrap gap-2 mt-10 pt-8 border-t border-surface-100">
                @for (tag of post.tags; track tag) {
                  <a [routerLink]="'/blog'" [queryParams]="{ tag: tag }"
                     class="px-3.5 py-1.5 bg-surface-50 text-xs font-medium text-surface-500 rounded-lg border border-surface-100 hover:bg-surface-100 hover:text-surface-700 transition-colors">
                    #{{ tag }}
                  </a>
                }
              </div>
            </div>

            <!-- Sidebar -->
            <aside class="hidden lg:block">
              <div class="sticky top-24 space-y-8">
                <!-- Table of Contents -->
                @if (toc().length > 0) {
                  <div>
                    <h4 class="text-[0.65rem] font-bold uppercase tracking-wider text-surface-400 mb-4">In this article</h4>
                    <nav>
                      <ul class="space-y-2 border-l-2 border-surface-100">
                        @for (item of toc(); track item.id) {
                          <li>
                            <a [href]="'#' + item.id"
                               class="block pl-4 py-1 text-sm text-surface-500 hover:text-brand-600 transition-colors -ml-[2px] border-l-2 border-transparent hover:border-brand-500">
                              {{ item.text }}
                            </a>
                          </li>
                        }
                      </ul>
                    </nav>
                  </div>
                }

                <!-- Author Card -->
                <div class="p-5 rounded-xl bg-surface-50 border border-surface-100">
                  <div class="flex items-center gap-3 mb-3">
                    <div class="w-11 h-11 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-bold text-sm">
                      {{ post.author.initials }}
                    </div>
                    <div>
                      <div class="text-sm font-bold text-surface-900">{{ post.author.name }}</div>
                      <div class="text-xs text-surface-400">{{ post.author.role }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <!-- Prev / Next -->
      @if (prevPost || nextPost) {
        <section class="bg-surface-50 border-t border-surface-100">
          <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <div class="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-surface-200">
              @if (prevPost) {
                <a [routerLink]="'/blog/' + prevPost.slug" class="group py-8 pr-8">
                  <span class="text-[0.6rem] font-bold uppercase tracking-wider text-surface-400 mb-2 block">Previous Article</span>
                  <span class="font-heading text-base font-bold text-surface-900 group-hover:text-brand-600 transition-colors line-clamp-2">{{ prevPost.title }}</span>
                </a>
              } @else {
                <div class="py-8 pr-8"></div>
              }
              @if (nextPost) {
                <a [routerLink]="'/blog/' + nextPost.slug" class="group py-8 md:pl-8 text-right">
                  <span class="text-[0.6rem] font-bold uppercase tracking-wider text-surface-400 mb-2 block">Next Article</span>
                  <span class="font-heading text-base font-bold text-surface-900 group-hover:text-brand-600 transition-colors line-clamp-2">{{ nextPost.title }}</span>
                </a>
              }
            </div>
          </div>
        </section>
      }

      <!-- Related Posts -->
      @if (relatedPosts.length) {
        <section class="py-16 md:py-20 bg-white border-t border-surface-100">
          <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <div class="flex items-center justify-between mb-10">
              <h2 class="font-heading text-xl md:text-2xl font-bold text-surface-900">Related Articles</h2>
              <a routerLink="/blog" class="group inline-flex items-center gap-2 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors">
                All Posts
                <svg aria-hidden="true" class="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
              @for (rp of relatedPosts; track rp.slug) {
                <a [routerLink]="'/blog/' + rp.slug"
                   class="group rounded-xl overflow-hidden bg-white border border-surface-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div class="aspect-[16/10] overflow-hidden">
                    <img [src]="rp.image" [alt]="rp.title" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div class="p-5">
                    <div class="flex items-center gap-2 mb-2">
                      <span class="text-[0.6rem] font-bold uppercase tracking-wider text-brand-600">{{ rp.category }}</span>
                      <span class="text-xs text-surface-400">&middot; {{ rp.readTime }}</span>
                    </div>
                    <h3 class="font-heading text-sm font-bold text-surface-900 group-hover:text-brand-600 transition-colors line-clamp-2">{{ rp.title }}</h3>
                  </div>
                </a>
              }
            </div>
          </div>
        </section>
      }
    } @else {
      <!-- Not Found -->
      <section class="py-32 text-center -mt-20 pt-52">
        <div class="mx-auto max-w-md px-6">
          <div class="w-16 h-16 rounded-full bg-surface-100 text-surface-400 flex items-center justify-center mx-auto mb-6">
            <svg aria-hidden="true" class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
          </div>
          <h1 class="font-heading text-2xl font-bold text-surface-900 mb-3">Post Not Found</h1>
          <p class="text-surface-500 mb-6">The blog post you're looking for doesn't exist or may have been moved.</p>
          <a routerLink="/blog" class="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 text-white font-semibold text-sm rounded-lg hover:bg-brand-700 transition-colors">
            <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
            Browse All Posts
          </a>
        </div>
      </section>
    }
  `,
  styles: [`
    :host ::ng-deep .blog-prose {
      h2 {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--color-surface-900);
        margin-top: 2.5rem;
        margin-bottom: 1rem;
        line-height: 1.25;
        letter-spacing: -0.015em;
      }
      h3 {
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--color-surface-900);
        margin-top: 2rem;
        margin-bottom: 0.75rem;
      }
      p {
        color: var(--color-surface-600);
        line-height: 1.8;
        margin-bottom: 1.25rem;
        font-size: 1.0625rem;
      }
      blockquote {
        border-left: 3px solid var(--color-brand-500);
        padding: 1rem 0 1rem 1.5rem;
        margin: 2rem 0;
        font-style: italic;
        color: var(--color-surface-700);
        font-size: 1.125rem;
        line-height: 1.7;
        background: var(--color-surface-50);
        border-radius: 0 0.75rem 0.75rem 0;
        padding-right: 1.5rem;
      }
      ul, ol {
        padding-left: 1.5rem;
        margin-bottom: 1.25rem;
        color: var(--color-surface-600);
      }
      ul { list-style-type: disc; }
      ol { list-style-type: decimal; }
      li {
        margin-bottom: 0.5rem;
        line-height: 1.7;
        font-size: 1.0625rem;
      }
      a {
        color: var(--color-brand-600);
        text-decoration: underline;
        text-underline-offset: 2px;
      }
      a:hover { color: var(--color-brand-700); }
      img {
        border-radius: 0.75rem;
        margin: 2rem 0;
      }
      strong { color: var(--color-surface-900); }
    }
  `],
})
export class BlogPostComponent implements OnInit, AfterViewInit {
  private route = inject(ActivatedRoute);
  private seo = inject(SeoService);
  private config = inject(SiteConfigService);
  private platformId = inject(PLATFORM_ID);
  private el = inject(ElementRef);

  post: BlogPost | undefined;
  relatedPosts: BlogPost[] = [];
  prevPost: BlogPost | undefined;
  nextPost: BlogPost | undefined;
  toc = signal<TocItem[]>([]);

  ngOnInit(): void {
    const slug = this.route.snapshot.paramMap.get('slug');
    const allPosts = this.config.pages.blog.posts;
    this.post = allPosts.find(p => p.slug === slug);

    if (this.post) {
      this.seo.setTitle(this.post.title);
      this.seo.setDescription(this.post.excerpt);
      this.seo.setJsonLd(this.seo.getBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Blog', url: '/blog' },
        { name: this.post.title, url: '/blog/' + this.post.slug },
      ]));

      // Prev / Next
      const idx = allPosts.indexOf(this.post);
      if (idx > 0) this.prevPost = allPosts[idx - 1];
      if (idx < allPosts.length - 1) this.nextPost = allPosts[idx + 1];

      // Related posts — same category first, then fill
      this.relatedPosts = allPosts
        .filter(p => p.slug !== slug && p.category === this.post!.category)
        .slice(0, 3);
      if (this.relatedPosts.length < 3) {
        const more = allPosts
          .filter(p => p.slug !== slug && !this.relatedPosts.includes(p))
          .slice(0, 3 - this.relatedPosts.length);
        this.relatedPosts = [...this.relatedPosts, ...more];
      }

    }
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // Inject IDs into rendered h2 elements for TOC anchor links
    const h2s = this.el.nativeElement.querySelectorAll('.blog-prose h2');
    const items: TocItem[] = [];
    h2s.forEach((h2: HTMLElement, i: number) => {
      const id = h2.id || 'section-' + i;
      h2.id = id;
      items.push({ id, text: h2.textContent?.trim() || '' });
    });
    if (items.length) {
      this.toc.set(items);
    }
  }

  sharePost(platform: string): void {
    if (!isPlatformBrowser(this.platformId) || !this.post) return;
    const url = window.location.href;
    const title = this.post.title;
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        break;
    }
  }
}
